import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

import { ref, reactive, computed, nextTick, watch } from 'vue'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import { useI18n } from 'vue-i18n'
import { BootItem, BootQueryParams, BootViewService } from '../boot-view/boot-view.service'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { ElMessage } from 'element-plus'
import { TableViewMode, TableViewModeType } from '@/views/dashboard/_enum/table-view-mode'
import CmTable from '@/components/cmTable/index.vue'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import { FormType, ObjectFormItem } from '@/components/cmForm/types'
import { TableOpt } from '@/components/cmTable/types'

export type BootListInitialQueryPatch = {
  autorun_type?: number
  key_word?: string
}

const DefaultSort = 'not_optimize_count'


export default /*#__PURE__*/_defineComponent({
  __name: 'boot-list-top10',
  props: {
    viewMode: { default: TableViewMode.preview },
    initialQueryPatch: { default: undefined }
  },
  emits: ["detail", "refresh", "viewAll"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const injector = useReflectiveInjector([BootViewService])
const bootService = injector.get(BootViewService)
const tableRef = ref<any>(null)
const { refreshList } = useCmTableListRefresh(tableRef)
const pageOption = { pageNumName: 'page', pageSizeName: 'limit' }

const form = reactive<BootQueryParams>({
  key_word: '',
  autorun_type: undefined,
  page: 1,
  limit: props.viewMode === TableViewMode.preview ? 10 : 20,
  sort: DefaultSort,
  asc: false
})
const multipleSelection = ref<BootItem[]>([])

watch(
  () => props.initialQueryPatch,
  (patch) => {
    if (props.viewMode !== TableViewMode.full) return
    if (patch == null) {
      form.autorun_type = undefined
      form.key_word = ''
      form.page = 1
      form.sort = DefaultSort
      form.asc = false
      nextTick(() => void refreshList())
      return
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'autorun_type')) {
      const at = patch.autorun_type
      form.autorun_type = typeof at === 'number' && at >= 1 && at <= 4 ? at : undefined
    } else {
      form.autorun_type = undefined
    }
    if (Object.prototype.hasOwnProperty.call(patch, 'key_word')) {
      form.key_word = patch.key_word ?? ''
    }
    form.page = 1
    nextTick(() => void refreshList())
  },
  { deep: true, immediate: true },
)

watch(
  () => props.viewMode,
  (vm) => {
    form.limit = vm === TableViewMode.preview ? 10 : 20
  }
)

const { t, locale } = useI18n()

async function handleQuery() {
  await refreshList()
}

const formColumns = computed<ObjectFormItem<typeof form, 'autorun_type'>>(() => {
  void locale.value
  return {
    autorun_type: {
      prop: 'autorun_type',
      label: t('bootOptimize.autorunType'),
      type: FormType.Select,
      colOpt: { style: 'width: 180px; flex: 0 0 auto' },
      hiddenAllLabel: true,
      outSearchFiledFlag: true,
      dictData: [
        { label: t('softwareManage.softTypeAll'), value: undefined },
        { label: t('bootOptimize.bootTypeMenu'), value: 1 },
        { label: t('bootOptimize.bootTypeTask'), value: 2 },
        { label: t('bootOptimize.bootTypeRegSoft'), value: 3 },
        { label: t('bootOptimize.bootTypeRegService'), value: 4 }
      ],
      events: { change: handleQuery }
    }
  }
})

const bootTypeDict = computed(() => ['', t('bootOptimize.bootTypeMenu'), t('bootOptimize.bootTypeTask'), t('bootOptimize.bootTypeRegSoft'), t('bootOptimize.bootTypeRegService')])
const bootSuggestDict = computed(() => ['', t('bootOptimize.suggestClose'), t('bootOptimize.suggestCareful')])
const tableOpt = computed<TableOpt>(() => {
  void locale.value
  return {
  bodyStyle: props.viewMode === TableViewMode.full ? 'padding: 10px 16px' : undefined,
  columns: [
    { prop: 'app_name', label: t('bootOptimize.bootItem') },
    { prop: 'soft_name', label: t('bootOptimize.appName') },
    { prop: 'description', label: t('bootOptimize.description') },
    { prop: 'autorun_type', label: t('bootOptimize.autorunType'), slotName: 'autorun_type' },
    { prop: 'suggest_action', label: t('bootOptimize.suggestAction'), slotName: 'suggest_action' },
    { prop: 'not_optimize_count', label: t('bootOptimize.notOptimizedTerminals'), slotName: 'not_optimize_count', sortable: 'custom' },
    { prop: 'optimize_count', label: t('bootOptimize.optimizedTerminals'), slotName: 'optimize_count', sortable: 'custom' }
  ],
  selection: true,
  usePagination: false,
  showSearch: false,
  showForm: false,
  showActionBar: false,
  showRightToolbar: true,
  rowKey: 'autorun_hash',
  path: 'list',
  totalPath: 'total',
  height: props.viewMode === TableViewMode.preview ? undefined : '100%',
  disableScrollLoad: props.viewMode === TableViewMode.preview,
  hiddenPageTool: props.viewMode === TableViewMode.preview
  }
})

function getListApi(params: any) {
  const query: BootQueryParams = {
    key_word: params.key_word || '',
    page: params.page || 1,
    limit: params.limit || (props.viewMode === TableViewMode.preview ? 10 : 20),
    sort: params.sort || DefaultSort,
    asc: params.asc || false
  }
  const at = params.autorun_type
  if (typeof at === 'number' && at >= 1 && at <= 4) {
    query.autorun_type = at
  }
  return bootService.fetchBootList(query)
}

async function handleSearch(value: string) {
  form.key_word = value
  await refreshList()
}

function handleReset() {
  form.key_word = ''
  form.autorun_type = undefined
  form.page = 1
  form.sort = DefaultSort
  form.asc = false
  void refreshList()
}

function handleViewAll() {
  emit('viewAll')
}

function calcActionDisabledStatus(type: string) {
  if (!multipleSelection.value.length) return true
  if (type === 'optimized') {
    return multipleSelection.value.some(item => !item.not_optimize_count)
  }
  if (type === 'nOptimized') {
    return multipleSelection.value.some(item => !item.optimize_count)
  }
  return true
}

function handleSelectionChange(selection: BootItem[], _ids: string[]) {
  multipleSelection.value = selection
}

function openDrawer(row: BootItem, optimized: number) {
  emit('detail', { row, optimized })
}

function optimizeTer(flag: boolean) {
  const ids = multipleSelection.value.map(item => item.autorun_hash)
  bootService.optimizeOrder({ autorun_list: ids, recovery: flag }).then(res => {
    tableRef.value?.tableRef?.clearSelection()
    multipleSelection.value = []
    ElMessage.success(
      flag
        ? t('bootOptimize.restoreDispatchSuccess', { terminalCount: res.terminal_count, itemCount: ids.length })
        : t('bootOptimize.optimizeDispatchSuccess', { terminalCount: res.terminal_count, itemCount: ids.length })
    )
    void refreshList()
  }).catch(() => {
    ElMessage.error(flag ? t('bootOptimize.restoreDispatchFailed', { itemCount: ids.length }) : t('bootOptimize.optimizeDispatchFailed', { itemCount: ids.length }))
  })
}

function trustTer() {
  const ids = multipleSelection.value.map(item => item.autorun_hash)
  bootService.trustOrder({ autorun_list: ids, trust: true }).then(() => {
    ElMessage.success(
      ids.length > 1
        ? t('bootOptimize.trustManyBootItems', { count: ids.length })
        : t('bootOptimize.trustOneBootItem', { name: multipleSelection.value[0].app_name || '' })
    )
    tableRef.value?.tableRef?.clearSelection()
    multipleSelection.value = []
    void refreshList()
    emit('refresh')
  }).catch(() => {
    ElMessage.error(t('bootOptimize.trustBootItemsFailed', { count: ids.length }))
  })
}

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!

  return (_openBlock(), _createBlock(CmTable, {
    ref_key: "tableRef",
    ref: tableRef,
    "table-opt": tableOpt.value,
    api: getListApi,
    modelValue: form,
    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form) = $event)),
    "page-option": pageOption,
    "first-loading": true,
    onChangeSelected: handleSelectionChange
  }, {
    header: _withCtx(() => [
      _createVNode(SearchFilterBar, {
        modelValue: form,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form) = $event)),
        "form-columns": formColumns.value,
        "show-search": true,
        "search-placeholder": _ctx.$t('bootOptimize.searchPlaceholderApp'),
        "popover-threshold": 0,
        onSearch: handleSearch,
        onQuery: handleQuery,
        onReset: handleReset
      }, {
        actions: _withCtx(() => [
          _createVNode(_component_el_button, {
            disabled: calcActionDisabledStatus('optimized'),
            type: "primary",
            size: "small",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (optimizeTer(false)))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('bootOptimize.optimizeItem')), 1)
            ]),
            _: 1
          }, 8, ["disabled"]),
          _createVNode(_component_el_button, {
            disabled: calcActionDisabledStatus('nOptimized'),
            type: "primary",
            size: "small",
            plain: "",
            onClick: _cache[1] || (_cache[1] = ($event: any) => (optimizeTer(true)))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('bootOptimize.restoreItem')), 1)
            ]),
            _: 1
          }, 8, ["disabled"]),
          _createVNode(_component_el_button, {
            disabled: !multipleSelection.value.length,
            type: "primary",
            size: "small",
            plain: "",
            onClick: trustTer
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('bootOptimize.trustItem')), 1)
            ]),
            _: 1
          }, 8, ["disabled"]),
          (_ctx.viewMode === _unref(TableViewMode).preview)
            ? (_openBlock(), _createBlock(_component_el_button, {
                key: 0,
                type: "primary",
                size: "small",
                plain: "",
                onClick: handleViewAll
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('commonPage.viewAll')), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["modelValue", "form-columns", "search-placeholder"])
    ]),
    autorun_type: _withCtx(({ row }: any) => [
      _createTextVNode(_toDisplayString(bootTypeDict.value[(row as BootItem).autorun_type]), 1)
    ]),
    suggest_action: _withCtx(({ row }: any) => [
      _createTextVNode(_toDisplayString(bootSuggestDict.value[(row as BootItem).suggest_action]), 1)
    ]),
    not_optimize_count: _withCtx(({ row }: any) => [
      _createVNode(_component_el_button, {
        onClick: ($event: any) => (openDrawer(row as BootItem, 2)),
        disabled: !(row as BootItem).not_optimize_count,
        type: "text",
        size: "small"
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString((row as BootItem).not_optimize_count), 1)
        ]),
        _: 2
      }, 1032, ["onClick", "disabled"])
    ]),
    optimize_count: _withCtx(({ row }: any) => [
      _createVNode(_component_el_button, {
        onClick: ($event: any) => (openDrawer(row as BootItem, 1)),
        disabled: !(row as BootItem).optimize_count,
        type: "text",
        size: "small"
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString((row as BootItem).optimize_count), 1)
        ]),
        _: 2
      }, 1032, ["onClick", "disabled"])
    ]),
    _: 1
  }, 8, ["table-opt", "modelValue"]))
}
}

})